/**
 * Date parsing and formatting utilities
 */

/**
 * Parse a date string or Date object, ensuring proper UTC handling
 * If the date string is in ISO format but missing 'Z' (indicating UTC),
 * append 'Z' to ensure it's treated as UTC time
 * Returns null if input is invalid
 */
export function parseDate(date: string | Date | null | undefined): Date | null {
  if (date == null) {
    return null;
  }

  if (typeof date === 'string') {
    // If the date string is in ISO format but missing 'Z' (indicating UTC),
    // append 'Z' to ensure it's treated as UTC time
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?$/.test(date)) {
      const parsed = new Date(date + 'Z');
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return isNaN(date.getTime()) ? null : date;
}

/**
 * Pad a number with leading zeros
 */
function padZero(num: number, length: number = 2): string {
  return num.toString().padStart(length, '0');
}

/**
 * Format date only: "MM/dd/yyyy"
 * Example: "01/15/2024"
 * Returns empty string if date is invalid
 */
export function formatDate(date: string | Date | null | undefined): string {
  const dateObj = parseDate(date);
  if (!dateObj) {
    return '';
  }
  const month = padZero(dateObj.getMonth() + 1);
  const day = padZero(dateObj.getDate());
  const year = dateObj.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Format date and time: "MM/dd/yyyy HH:mm:ss"
 * Example: "01/15/2024 14:30:45"
 * Returns empty string if date is invalid
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  const dateObj = parseDate(date);
  if (!dateObj) {
    return '';
  }
  const month = padZero(dateObj.getMonth() + 1);
  const day = padZero(dateObj.getDate());
  const year = dateObj.getFullYear();
  const hours = padZero(dateObj.getHours());
  const minutes = padZero(dateObj.getMinutes());
  const seconds = padZero(dateObj.getSeconds());
  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Format time only: "HH:mm:ss"
 * Example: "14:30:45"
 * Returns empty string if date is invalid
 */
export function formatTime(date: string | Date | null | undefined): string {
  const dateObj = parseDate(date);
  if (!dateObj) {
    return '';
  }
  const hours = padZero(dateObj.getHours());
  const minutes = padZero(dateObj.getMinutes());
  const seconds = padZero(dateObj.getSeconds());
  return `${hours}:${minutes}:${seconds}`;
}


